import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AgentPicker } from '@/components/AgentPicker'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Field, Input, Select } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton, Spinner } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { Separator } from '@/ui/Separator/Separator'
import { ApiError, NetworkError } from '../api/client'
import { topicsApi } from '../api/topics'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { topicsCopy } from '../copy'
import type { RejectedWrites } from '../copy/topics'
import {
  topicQueryKeys,
  useMeQuery,
  useTopicQuery,
  useWorkspaceAgentsQuery,
} from '../hooks/queries'
import { useGoBack } from '../hooks/useGoBack'
import { useMutationToast } from '../hooks/useMutationToast'
import type { TopicInfo, UpdateTopicAgentsBody } from '../types/api'
import { validateTopicName } from '../utils/topicNames'
import styles from './TopicSettingsPage.module.css'

type OutcomeKind = 'success' | 'rejected' | 'unknown'

interface OperationOutcome {
  kind: OutcomeKind
  message?: string
}

interface SaveAttempt {
  rename?: {
    payload: string
    outcome?: OperationOutcome
  }
  membership?: {
    payload: UpdateTopicAgentsBody
    outcome?: OperationOutcome
  }
}

interface SettingsSnapshot {
  name: string
  agentIds: string[]
}

type Feedback = { kind: 'error' | 'status'; message: string }

function isUnknownWrite(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    (error instanceof ApiError && error.status >= 500 && !error.isStructured)
  )
}

function outcomeFor(error: unknown): OperationOutcome {
  return {
    kind: isUnknownWrite(error) ? 'unknown' : 'rejected',
    message:
      error instanceof Error
        ? error.message
        : topicsCopy.settings.requestFailed,
  }
}

function sameMembers(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false
  const rightSet = new Set(right)
  return left.every((id) => rightSet.has(id))
}

function outcomeLabel(outcome: OperationOutcome | undefined): string {
  if (!outcome) return topicsCopy.settings.notSent
  if (outcome.kind === 'success') return topicsCopy.settings.confirmedSuccess
  if (outcome.kind === 'rejected') return topicsCopy.settings.confirmedRejection
  return topicsCopy.settings.statusUnknown
}

export function TopicSettingsPage() {
  const { topicId } = useParams<{ topicId: string }>()
  const navigate = useNavigate()
  const goBack = useGoBack(topicId ? ROUTES.topic(topicId) : ROUTES.home)
  const queryClient = useQueryClient()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [baseline, setBaseline] = useState<SettingsSnapshot | null>(null)
  const [name, setName] = useState('')
  const [selectedAgents, setSelectedAgents] = useState<string[]>([])
  const [nameError, setNameError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [attempt, setAttempt] = useState<SaveAttempt | null>(null)
  const [saving, setSaving] = useState(false)
  const [savingPrimary, setSavingPrimary] = useState(false)
  const [primaryDraft, setPrimaryDraft] = useState<string | null>(null)
  const [primaryError, setPrimaryError] = useState<string | null>(null)
  const seededTopicRef = useRef<string | null>(null)
  const previousTopicRef = useRef<string | undefined>(topicId)

  const topicQuery = useTopicQuery(topicId)
  const topic = topicQuery.data
  const workspaceId = topic?.workspace_id
  const agentsQuery = useWorkspaceAgentsQuery(workspaceId, {
    enabled: topicQuery.isSuccess,
    staleTime: 30_000,
  })
  const agents = agentsQuery.data
  const { data: me } = useMeQuery()
  const workspaceName =
    me?.workspaces.find((workspace) => workspace.workspace_id === workspaceId)
      ?.name ?? topicsCopy.workspaceCrumb

  // The trail is hierarchy, known before the topic loads: the topic's own name
  // stands in until it arrives.
  const crumbs = [
    { label: workspaceName, to: ROUTES.home },
    {
      label: topic?.name ?? topicsCopy.settings.topicCrumb,
      to: topicId ? ROUTES.topic(topicId) : undefined,
    },
    { label: topicsCopy.settings.settingsCrumb },
  ]

  const seedFromTopic = useCallback((nextTopic: TopicInfo) => {
    const snapshot = {
      name: nextTopic.name,
      agentIds: nextTopic.agents.map((agent) => agent.agent_id),
    }
    setBaseline(snapshot)
    setName(snapshot.name)
    setSelectedAgents(snapshot.agentIds)
    setNameError(null)
  }, [])

  useEffect(() => {
    if (previousTopicRef.current === topicId) return
    previousTopicRef.current = topicId
    seededTopicRef.current = null
    setBaseline(null)
    setName('')
    setSelectedAgents([])
    setNameError(null)
    setFeedback(null)
    setAttempt(null)
  }, [topicId])

  useEffect(() => {
    if (
      !topicId ||
      !topic ||
      !topicQuery.isSuccess ||
      !agentsQuery.isSuccess ||
      seededTopicRef.current === topicId
    ) {
      return
    }
    seededTopicRef.current = topicId
    seedFromTopic(topic)
  }, [
    agentsQuery.isSuccess,
    seedFromTopic,
    topic,
    topicId,
    topicQuery.isSuccess,
  ])

  const deleteMutation = useMutationToast({
    mutationFn: () => {
      if (!topicId) throw new Error('Topic is unavailable')
      return topicsApi.delete(topicId)
    },
    errorMessage: topicsCopy.deleteFailed,
    onSuccess: () => {
      if (workspaceId) {
        void queryClient.invalidateQueries({
          queryKey: topicQueryKeys.workspace(workspaceId),
        })
      }
      if (topicId) {
        void queryClient.removeQueries({
          queryKey: topicQueryKeys.detail(topicId),
        })
      }
      void navigate(ROUTES.home)
    },
  })

  async function executeAttempt(
    current: SaveAttempt,
    retryUnknownOnly: boolean,
  ): Promise<SaveAttempt> {
    if (!topicId) return current

    const operations: Array<{
      key: 'rename' | 'membership'
      promise: Promise<unknown>
    }> = []
    if (
      current.rename &&
      (!retryUnknownOnly || current.rename.outcome?.kind === 'unknown')
    ) {
      operations.push({
        key: 'rename',
        promise: topicsApi.rename(topicId, current.rename.payload),
      })
    }
    if (
      current.membership &&
      (!retryUnknownOnly || current.membership.outcome?.kind === 'unknown')
    ) {
      operations.push({
        key: 'membership',
        promise: topicsApi.updateAgents(topicId, current.membership.payload),
      })
    }

    const settled = await Promise.allSettled(
      operations.map((operation) => operation.promise),
    )
    const next: SaveAttempt = {
      rename: current.rename ? { ...current.rename } : undefined,
      membership: current.membership
        ? {
            ...current.membership,
            payload: {
              add: [...current.membership.payload.add],
              remove: [...current.membership.payload.remove],
            },
          }
        : undefined,
    }

    settled.forEach((result, index) => {
      const outcome: OperationOutcome =
        result.status === 'fulfilled'
          ? { kind: 'success' }
          : outcomeFor(result.reason)
      const operation = operations[index]
      if (operation.key === 'rename' && next.rename) {
        next.rename.outcome = outcome
      } else if (operation.key === 'membership' && next.membership) {
        next.membership.outcome = outcome
      }
    })
    return next
  }

  function outcomesOf(current: SaveAttempt): OperationOutcome[] {
    return [current.rename?.outcome, current.membership?.outcome].filter(
      (outcome): outcome is OperationOutcome => !!outcome,
    )
  }

  async function syncAfterRejection(current: SaveAttempt) {
    if (!topicId) return
    const renameRejected = current.rename?.outcome?.kind === 'rejected'
    const membershipRejected = current.membership?.outcome?.kind === 'rejected'
    const rejected: RejectedWrites =
      renameRejected && membershipRejected
        ? 'both'
        : renameRejected
          ? 'rename'
          : 'membership'

    try {
      const fresh = await topicsApi.get(topicId, { noCache: true })
      queryClient.setQueryData(topicQueryKeys.detail(topicId), fresh)
      seedFromTopic(fresh)
      setAttempt(null)
      setFeedback({
        kind: 'error',
        message: topicsCopy.settings.rejected(rejected, true),
      })
      void queryClient.invalidateQueries({
        queryKey: topicQueryKeys.workspace(fresh.workspace_id),
      })
    } catch {
      setAttempt(null)
      setFeedback({
        kind: 'error',
        message: topicsCopy.settings.rejected(rejected, false),
      })
    }
  }

  async function finishAttempt(current: SaveAttempt) {
    const outcomes = outcomesOf(current)
    if (outcomes.some((outcome) => outcome.kind === 'unknown')) {
      setAttempt(current)
      setFeedback({
        kind: 'status',
        message: topicsCopy.settings.outcomeUnknown,
      })
      return
    }
    if (outcomes.every((outcome) => outcome.kind === 'success')) {
      if (workspaceId) {
        await queryClient.invalidateQueries({
          queryKey: topicQueryKeys.workspace(workspaceId),
        })
      }
      if (topicId) {
        await queryClient.invalidateQueries({
          queryKey: topicQueryKeys.detail(topicId),
        })
        void navigate(ROUTES.topic(topicId))
      }
      return
    }
    await syncAfterRejection(current)
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!topicId || !baseline || !agentsQuery.isSuccess || saving || attempt)
      return

    const validation = validateTopicName(name)
    if (!validation.ok) {
      setNameError(
        validation.reason === 'empty'
          ? topicsCopy.nameEmpty
          : topicsCopy.nameTooLong,
      )
      queueMicrotask(() => document.getElementById('topic-name')?.focus())
      return
    }

    // Preserve authoritative assignments that a stale workspace-agent cache
    // does not currently expose. Only an explicit user toggle should remove a
    // participant from the desired set.
    const desiredAgents = selectedAgents
    const renameNeeded = validation.name !== baseline.name
    const membershipNeeded = !sameMembers(desiredAgents, baseline.agentIds)
    if (!renameNeeded && !membershipNeeded) {
      goBack()
      return
    }

    const nextAttempt: SaveAttempt = {
      rename: renameNeeded ? { payload: validation.name } : undefined,
      membership: membershipNeeded
        ? {
            payload: {
              add: desiredAgents.filter(
                (id) => !baseline.agentIds.includes(id),
              ),
              remove: baseline.agentIds.filter(
                (id) => !desiredAgents.includes(id),
              ),
            },
          }
        : undefined,
    }

    setNameError(null)
    setFeedback(null)
    setSaving(true)
    try {
      const completed = await executeAttempt(nextAttempt, false)
      await finishAttempt(completed)
    } finally {
      setSaving(false)
    }
  }

  async function retryUnknown() {
    if (!attempt || saving) return
    setSaving(true)
    setFeedback(null)
    try {
      const completed = await executeAttempt(attempt, true)
      await finishAttempt(completed)
    } finally {
      setSaving(false)
    }
  }

  function toggleAgent(agentId: string) {
    setSelectedAgents((current) =>
      current.includes(agentId)
        ? current.filter((id) => id !== agentId)
        : [...current, agentId],
    )
  }

  /** The primary must be an active definition; paused members are listed for
   * transparency but the backend would reject selecting them. */
  const primaryCandidates = (agents ?? []).filter(
    (agent) =>
      selectedAgents.includes(agent.agent_id) && agent.status === 'active',
  )
  async function applyPrimary(agentId: string | null) {
    if (!topicId || savingPrimary) return
    setSavingPrimary(true)
    setPrimaryError(null)
    setPrimaryDraft(agentId)
    try {
      await topicsApi.setPrimaryAgent(topicId, agentId)
      if (workspaceId) {
        void queryClient.invalidateQueries({
          queryKey: topicQueryKeys.workspace(workspaceId),
        })
      }
      await queryClient.invalidateQueries({
        queryKey: topicQueryKeys.detail(topicId),
      })
      setPrimaryDraft(null)
    } catch (error) {
      setPrimaryDraft(null)
      setPrimaryError(
        error instanceof Error
          ? error.message
          : topicsCopy.settings.setPrimaryFailed,
      )
    } finally {
      setSavingPrimary(false)
    }
  }

  const showSkeleton = useSkeletonDelay(
    topicQuery.isPending || agentsQuery.isPending || !baseline,
  )

  if (!topicId) return null

  if (topicQuery.isPending) {
    return (
      <WsPageShell crumbs={crumbs} title={topicsCopy.settings.title}>
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      </WsPageShell>
    )
  }

  if (!topic) {
    return (
      <WsPageShell crumbs={crumbs} title={topicsCopy.settings.title}>
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>{topicsCopy.settings.loadFailed}</Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void topicQuery.refetch()}
            >
              {topicsCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </WsPageShell>
    )
  }

  if (agentsQuery.isError && !agents) {
    return (
      <WsPageShell crumbs={crumbs} title={topicsCopy.settings.title}>
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>{topicsCopy.settings.agentsLoadFailed}</Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void agentsQuery.refetch()}
            >
              {topicsCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </WsPageShell>
    )
  }

  if (agentsQuery.isPending || !agents || !baseline) {
    return (
      <WsPageShell crumbs={crumbs} title={topicsCopy.settings.title}>
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      </WsPageShell>
    )
  }

  const unknownPending = outcomesOf(attempt ?? {}).some(
    (outcome) => outcome.kind === 'unknown',
  )
  const frozen = saving || unknownPending
  // A failed agents refetch keeps the old list; membership and the submit
  // handler (which requires a fresh list) wait for it.
  const agentsStale = !agentsQuery.isSuccess

  return (
    <WsPageShell
      crumbs={crumbs}
      title={topicsCopy.settings.title}
      description={topicsCopy.settings.description}
    >
      <Stack gap="space-16" className={styles.column}>
        {topicQuery.isError && (
          <Alert tone="warning">
            <Inline gap="space-8" align="baseline">
              <Text>{topicsCopy.refreshTopicFailed}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void topicQuery.refetch()}
              >
                {topicsCopy.retryTopic}
              </Button>
            </Inline>
          </Alert>
        )}
        {agentsQuery.isError && (
          <Alert tone="warning">
            <Inline gap="space-8" align="baseline">
              <Text>{topicsCopy.refreshAgentsFailed}</Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void agentsQuery.refetch()}
              >
                {topicsCopy.retryAgents}
              </Button>
            </Inline>
          </Alert>
        )}
        <form onSubmit={(event) => void handleSubmit(event)}>
          <Stack gap="space-16">
            <Field
              id="topic-name"
              label={topicsCopy.nameLabel}
              hint={topicsCopy.requiredHint}
              error={nameError ?? undefined}
              required
              className={styles.name}
            >
              <Input
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                  setNameError(null)
                  setFeedback(null)
                }}
                autoFocus
                disabled={frozen}
              />
            </Field>

            <AgentPicker
              legend={topicsCopy.settings.agentsLegend}
              agents={agents}
              selected={selectedAgents}
              onToggle={toggleAgent}
              locked={frozen || agentsStale}
              lockedReason={agentsStale ? topicsCopy.agentsLocked : undefined}
              emptyText={topicsCopy.settings.agentsEmpty}
            />

            {topic.primary_agent_id && (
              <Text as="p" variant="caption" tone="text-mute">
                {topicsCopy.settings.currentPrimary(
                  agents.find(
                    (agent) => agent.agent_id === topic.primary_agent_id,
                  )?.name ?? topic.primary_agent_id,
                )}
              </Text>
            )}

            <Field
              id="topic-primary-agent"
              label={topicsCopy.primaryLabel}
              hint={savingPrimary ? <Spinner /> : undefined}
              help={topicsCopy.primaryHelp}
              error={primaryError ?? undefined}
              className={styles.select}
            >
              <Select
                value={primaryDraft ?? topic.primary_agent_id ?? ''}
                disabled={frozen}
                aria-busy={savingPrimary || undefined}
                onChange={(event) =>
                  void applyPrimary(
                    event.target.value === '' ? null : event.target.value,
                  )
                }
              >
                <option value="">{topicsCopy.settings.primaryNone}</option>
                {primaryCandidates.map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.name}
                  </option>
                ))}
              </Select>
            </Field>

            {feedback && (
              <Alert tone={feedback.kind === 'error' ? 'danger' : 'warning'}>
                {feedback.message}
              </Alert>
            )}

            {attempt && (
              <Card>
                <Stack gap="space-8" align="start">
                  <Text tone="text-sub">
                    {topicsCopy.settings.reconciliation}
                  </Text>
                  <Stack as="ul" gap="space-4" className={styles.list}>
                    {attempt.rename && (
                      <Text as="li" variant="caption" tone="text-mute">
                        {topicsCopy.settings.renameOutcome(
                          outcomeLabel(attempt.rename.outcome),
                        )}
                      </Text>
                    )}
                    {attempt.membership && (
                      <Text as="li" variant="caption" tone="text-mute">
                        {topicsCopy.settings.membershipOutcome(
                          outcomeLabel(attempt.membership.outcome),
                        )}
                      </Text>
                    )}
                  </Stack>
                  {unknownPending && (
                    <Button
                      type="button"
                      variant="outline"
                      ground="surface"
                      loading={saving}
                      onClick={() => void retryUnknown()}
                    >
                      {topicsCopy.settings.retryRequests}
                    </Button>
                  )}
                </Stack>
              </Card>
            )}

            <Inline gap="space-8">
              <Button
                type="submit"
                variant="primary"
                disabled={frozen || agentsStale || !validateTopicName(name).ok}
                loading={saving}
              >
                {topicsCopy.settings.save}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={goBack}
                disabled={saving}
              >
                {topicsCopy.cancel}
              </Button>
            </Inline>
          </Stack>
        </form>

        <Stack gap="space-24">
          <Separator />
          <div>
            <Button
              variant="outline"
              disabled={frozen}
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Icon name="trash" size="icon-embedded" />
              {topicsCopy.settings.delete}
            </Button>
          </div>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={topicsCopy.settings.deleteTitle}
        tone="danger"
        description={topicsCopy.settings.deleteDescription}
        confirmLabel={topicsCopy.settings.deleteConfirm}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </WsPageShell>
  )
}
